import { Plus, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/Button'
import { Checkbox, Field } from '@/components/ui/Field'
import { IconButton } from '@/components/ui/IconButton'
import { Input } from '@/components/ui/Input'
import { PhoneInput } from '@/components/ui/PhoneInput'
import { emptyContact, type ContactPayload } from '@/features/directories/api/useDirectory'

/* The contacts block, shared by the client and participant sheets (FR-39, FR-41).
 *
 * One component rather than two copies, because the fields are identical and
 * the rules that matter — messenger handles are optional, at most one primary,
 * nothing is sent to a messenger — are the same for both.
 *
 * Suppressed values never reach an input. A role that may not read a phone
 * number gets an empty, disabled field rather than the marker object: writing
 * the marker back would replace a real number with "hidden" (AD-6). The API
 * layer strips it too, so this is the visible half of a guarantee enforced in
 * two places on purpose.
 */

export interface ContactsEditorProps {
  contacts: ContactPayload[]
  onChange: (contacts: ContactPayload[]) => void
  /** False when the caller's role may not read contact details (NFR-3). */
  editable: boolean
}

export function ContactsEditor({ contacts, onChange, editable }: ContactsEditorProps) {
  const { t } = useTranslation()

  const update = (index: number, patch: Partial<ContactPayload>) => {
    onChange(contacts.map((c, i) => (i === index ? { ...c, ...patch } : c)))
  }

  /* Exactly one primary. Setting one clears the others here rather than letting
     the server refuse: the database enforces it with a partial unique index, and
     a form that submits two and then explains the refusal is a form that made
     the user do arithmetic it could have done itself. */
  const setPrimary = (index: number) => {
    onChange(contacts.map((c, i) => ({ ...c, isPrimary: i === index })))
  }

  return (
    <fieldset className="flex flex-col gap-3">
      <legend className="text-text-body pb-1 text-[12.5px] leading-none">
        {t('directories.contacts.legend')}
      </legend>

      {!editable ? (
        <p className="text-text-muted text-[12.5px] leading-snug font-light">
          {t('directories.contacts.restricted')}
        </p>
      ) : null}

      {contacts.map((contact, index) => (
        <div
          key={index}
          className="border-line flex flex-col gap-2.5 rounded-md border p-3"
        >
          <div className="flex items-center gap-2">
            <Input
              value={contact.label}
              disabled={!editable}
              placeholder={t('directories.contacts.labelPlaceholder')}
              aria-label={t('directories.contacts.label')}
              onChange={(e) => update(index, { label: e.target.value })}
              className="flex-1"
            />
            <IconButton
              icon={Trash2}
              label={t('directories.contacts.remove')}
              disabled={!editable}
              onClick={() => onChange(contacts.filter((_, i) => i !== index))}
            />
          </div>

          <div className="grid gap-2.5 sm:grid-cols-2">
            <Field label={t('common.fields.email')}>
              {(props) => (
                <Input
                  type="email"
                  inputMode="email"
                  value={contact.email}
                  disabled={!editable}
                  onChange={(e) => update(index, { email: e.target.value })}
                  {...props}
                />
              )}
            </Field>

            {/* The calling code is a picker rather than something the typist has
                to remember, which is what stops the same mobile being filed as
                "+7 701 …", "8 701 …" and "701 …". The stored value is still one
                string — see PhoneInput. */}
            <Field label={t('common.fields.phone')}>
              {(props) => (
                <PhoneInput
                  value={contact.phone}
                  disabled={!editable}
                  onChange={(phone) => update(index, { phone })}
                  {...props}
                />
              )}
            </Field>
          </div>

          {/* FR-41. Contact information only — the platform never sends to a
              messenger, and the hint says so, because a field that looks like a
              delivery channel will be treated as one. */}
          <div className="grid gap-2.5 sm:grid-cols-3">
            {(
              [
                ['wechat', 'WeChat'],
                ['telegram', 'Telegram'],
                ['whatsapp', 'WhatsApp'],
              ] as const
            ).map(([key, label]) => (
              <Field key={key} label={label}>
                {(props) => (
                  <Input
                    value={contact[key]}
                    disabled={!editable}
                    // Pasting a profile link is the common case; the server
                    // normalises it to the bare handle either way.
                    placeholder={t('directories.contacts.handlePlaceholder')}
                    onChange={(e) => update(index, { [key]: e.target.value })}
                    {...props}
                  />
                )}
              </Field>
            ))}
          </div>

          <p className="text-text-muted text-[12px] leading-snug font-light">
            {t('directories.contacts.messengerNote')}
          </p>

          <Checkbox
            label={t('directories.contacts.primary')}
            checked={contact.isPrimary}
            disabled={!editable}
            onCheckedChange={() => setPrimary(index)}
          />
        </div>
      ))}

      {editable ? (
        <Button
          type="button"
          variant="secondary"
          size="sm"
          iconLeft={Plus}
          className="self-start"
          onClick={() => onChange([...contacts, emptyContact()])}
        >
          {t('directories.contacts.add')}
        </Button>
      ) : null}
    </fieldset>
  )
}
